import { useEffect, useId, useRef } from 'react';
import type { Html5Qrcode as Html5QrcodeInstance } from 'html5-qrcode';

interface BarcodeCameraReaderProps {
  onDetected: (barcode: string) => void;
  onError: (error: unknown) => void;
}

/** Starts the rear camera only while this component is mounted. */
export default function BarcodeCameraReader({ onDetected, onError }: BarcodeCameraReaderProps) {
  const id = `barcode-camera-${useId().replace(/:/g, '')}`;
  const callbacks = useRef({ onDetected, onError });
  callbacks.current = { onDetected, onError };

  useEffect(() => {
    let disposed = false;
    let hasDetectedCode = false;
    let scanner: Html5QrcodeInstance | undefined;
    let shutdownPromise: Promise<void> | undefined;
    let startPromise: Promise<void> = Promise.resolve();
    let startTimer: number | undefined;

    const shutdown = () => {
      if (!scanner) return Promise.resolve();
      if (!shutdownPromise) {
        shutdownPromise = (async () => {
          try {
            if (scanner?.isScanning) await scanner.stop();
          } finally {
            scanner?.clear();
          }
        })();
      }
      return shutdownPromise;
    };

    // Deferring startup by one tick lets React StrictMode's development-only
    // effect cleanup cancel its first setup before it loads the scanner and
    // opens the camera. Keep the decoder out of the initial app bundle.
    startTimer = window.setTimeout(() => {
      startPromise = import('html5-qrcode').then(({ Html5Qrcode, Html5QrcodeSupportedFormats }) => {
        if (disposed) return undefined;
        scanner = new Html5Qrcode(id, {
          verbose: false,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.ITF,
          ],
        });
        return scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: (width, height) => ({
              width: Math.floor(Math.min(width * 0.9, 320)),
              height: Math.floor(Math.min(height * 0.4, 140)),
            }),
          },
          (decodedText) => {
            if (!disposed && !hasDetectedCode) {
              hasDetectedCode = true;
              callbacks.current.onDetected(decodedText);
            }
          },
          () => undefined,
        );
      }).then(async () => {
        if (disposed) await shutdown();
      }).catch((error: unknown) => {
        if (!disposed) callbacks.current.onError(error);
      });
    }, 0);

    return () => {
      disposed = true;
      if (startTimer !== undefined) window.clearTimeout(startTimer);
      void startPromise.then(shutdown).catch(() => undefined);
    };
  }, [id]);

  return (
    <div
      id={id}
      style={{ width: '100%' }}
      aria-label="Barcode camera preview"
    />
  );
}
